<script lang="ts">
    export let label:string;
    export let value: number;
    export let name:string;
    export let min:number = 0;
    export let max:number = 100;
    export let step:number = 1;
    export let style:rangeStyle = 'default';

    import { createEventDispatcher } from 'svelte';
    import { prefs } from '../stores.js';

    type rangeStyle = 'hue' | 'saturation' | 'default'

    if (value === undefined || null) {
        value = 0;
    }

    const dispatch = createEventDispatcher();
    function inputChange() {
		dispatch('message', {
			text: 'Hello!'
		});
    }

    // CALLING THIS ON KEYUP IS PROBLEMATIC BECAUSE I WANT TO ALLOW INTs STARTING WITH 1
    function enforceMinMax(){
        if (value < min) {
            value = min;
            value = value;
        }
        if (value > max) {
            value = max;
            value = value;
        }
        inputChange();
    }

</script>

<div class="container">
    <label for={name}>{label}</label>
    <div class="inputs">
        <input type="range" class:hue="{style === 'hue'}" name={name + '_range'} class:hct="{$prefs.colorSpace === 'HCT' && style === 'hue'}" bind:value={value} min={min} max={max} step={step} on:input={inputChange} />
        <input type="number" name={name} bind:value={value} min={min} max={max} maxlength="4" on:input={inputChange} on:keyup={enforceMinMax} /> 

    </div>
</div>


<style>

div.container {
    width: 100%;
    margin: 1rem 0;
}
div.inputs {
    display: flex;
}
label {
    font-size: 12px;
    width: 100%;
    display: block;
}
input[type="number"] {
    color: var(--text);
    background: var(--background);
    border: none;
    padding: 0.4rem 0;
    min-width: 4rem;
    width: 4rem;
    display: block;
    margin-left: 1rem;
}
input[type="range"] {
    width: 100%;
    height: 48px;
    transition: 1s;
}
input[type="range"]:hover {
    background-color: var(--background-offset);
}
@media (max-width: 960px) {
    input[type="number"] {
        border: none;
        padding: 0.4rem 0;
        min-width: 4rem;
        flex-grow: 0;
        display: inline-block;
        
    }
    input[type="range"] {
        display: inline-block;
        flex-grow: 1;
    }
}

@keyframes focus_color{ from { outline-color: var(--text); } to { outline-color: var(--background-offset); }  }

:root {
    --border-radius-big: 12px;
    --border-radius-default: 8px;
    --border-radius-small: 4px;
    --spectrum-gradient: linear-gradient(90deg, #FF0000, #FFFF00, #00FF00, #00FFFF, #0000FF, #FF00FF, #FF0000);
    --hct-spectrum-gradient: linear-gradient(90deg, #E7007D 0%, #B26300 14.4%, #078b00 37.8%, #008943 42.5%, #0074e9 73.3%, #9549ff 84.7%, #E7007D 100%);
}

/* fancy cross browser range code */

input[type=range] {
    width: 100%;
    margin: 0px 0;
    background-color: transparent;
    -webkit-appearance: none;
    border: none;
    padding: 0;
    border-radius: 4px;
}
input[type=range]:focus {
    outline: 2px solid var(--text);
    animation: focus_color 1s ease-in-out 0s infinite alternate;
}

/* WEBKIT ie Chrome and Safari */

input[type=range]::-webkit-slider-runnable-track {
    border: 0;
    border-radius: var(--border-radius-big);
    width: 100%;
    height: 2px;
    background-color: var(--text);
    cursor: pointer;
}
input[type=range]::-webkit-slider-thumb {
    margin-top: -24px;
    width: 4px;
    height: 48px;
    background: rgba(255, 255, 255, 0);
    border: 4px solid var(--text);
    box-sizing: border-box;
    border-radius: 4px;
    cursor: pointer;
    -webkit-appearance: none;
}
input[type=range]:focus::-webkit-slider-runnable-track {
    background: var(--background-offset);
}
input.hue[type=range]:focus::-webkit-slider-runnable-track {
    background: var(--spectrum-gradient);
}

input.hue.hct[type=range]:focus::-webkit-slider-runnable-track {
    background: var(--hct-spectrum-gradient);
}

/* Firefox */

input[type=range]::-moz-range-track {
    /* background: var(--background-offset); */
    border: 0;
    border-radius: var(--border-radius-small);
    width: 100%;
    /* height: 48px; */
    height: 2px;
    background-color: var(--text);
    cursor: pointer;
}
input.hue[type=range]:focus::-moz-range-track {
    height: 48px;
    background: var(--spectrum-gradient);
}
input.hue.hct[type=range]:focus::-moz-range-track {
    background: var(--hct-spectrum-gradient);
}
input[type=range]::-moz-range-thumb {
    width: 4px;
    height: 48px;
    background: rgba(255, 255, 255, 0);
    border: 4px solid var(--text);
    box-sizing: border-box;
    border-radius: 4px;
    cursor: pointer;
}

/* Internet Explorer */

input[type=range]::-ms-track {
    background: transparent;
    border-color: transparent;
    border-width: 1px 0;
    border-radius: var(--border-radius-big);
    color: transparent;
    width: 100%;
    height: 2px;
    cursor: pointer;
}
input[type=range]::-ms-fill-lower {
    background: var(--background-offset);
    border: 0;
    border-radius: 2.6px;
}
input[type=range]::-ms-fill-upper {
    background: var(--background-offset);
    border: 0;
    border-radius: 2.6px;
}
input[type=range]::-ms-thumb {
    width: 4px;
    height: 48px;
    background: rgba(255, 255, 255, 0);
    border: 4px solid var(--text)00;
    box-sizing: border-box;
    border-radius: 4px;
    cursor: pointer;
    margin-top: 0px;
    /*Needed to keep the Edge thumb centred*/
}
input[type=range]:focus::-ms-fill-lower {
    background: var(--background-offset);
}
input[type=range]:focus::-ms-fill-upper {
    background: var(--background-offset);
}

@supports (-ms-ime-align:auto) {
    /* Pre-Chromium Edge only styles, selector taken from hhttps://stackoverflow.com/a/32202953/7077589 */
    input[type=range] {
            margin: 0;
            /*Edge starts the margin from the thumb, not the track as other browsers do*/
    }
}

/* CSS specific to iOS devices */ 
@supports (-webkit-touch-callout: none) {
    input[type=range]::-webkit-slider-thumb {
        width: 48px;
    }
}

</style>